import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  ArrowRight, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  Users,
  Hash,
  Calendar as CalendarIcon,
  Sparkles,
  Eye
} from 'lucide-react';
import FileUpload from '../components/FileUpload';
import { plannerAPI } from '../api';
import { useCalendarStore } from '../store/calendarStore';

export default function NewPlan() {
  const navigate = useNavigate();
  const { setCurrentCalendar, addCalendar } = useCalendarStore();
  
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStage, setProgressStage] = useState(0);
  const [completedCalendar, setCompletedCalendar] = useState(null);
  const [personaWarning, setPersonaWarning] = useState('');
  const [options, setOptions] = useState({
    generateContent: true,
    weekStartDate: getNextMonday(),
    personaCount: 3,
    postsPerWeek: 5,
    commentsPerPost: 2
  });

  // Parse file mutation
  const parseMutation = useMutation({
    mutationFn: (file) => plannerAPI.parseFile(file),
    onSuccess: (response) => {
      setPreview(response.data);
      if (response.data.validation.valid) {
        setStep(2);
      } else {
        toast.error('File has validation errors');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to parse file');
    }
  });

  // Generate calendar mutation
  const generateMutation = useMutation({
    mutationFn: () => plannerAPI.generateFromFile(file, options),
    onSuccess: (response) => {
      const calendar = response.data;
      setCurrentCalendar(calendar);
      addCalendar({
        id: calendar.id,
        company: calendar.input?.company,
        createdAt: calendar.createdAt,
        summary: calendar.summary
      });
      setCompletedCalendar(calendar);
      setProgressStage(5); // Completed
    },
    onError: (error) => {
      setShowProgressModal(false);
      toast.error(error.response?.data?.error || 'Failed to generate calendar');
    }
  });

  // Progress stages simulation
  useEffect(() => {
    if (showProgressModal && generateMutation.isPending) {
      const stages = [1, 2, 3, 4];
      let currentIndex = 0;
      
      const interval = setInterval(() => {
        if (currentIndex < stages.length) {
          setProgressStage(stages[currentIndex]);
          currentIndex++;
        }
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [showProgressModal, generateMutation.isPending]);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setPreview(null);
  };

  const handleParse = () => {
    if (file) {
      parseMutation.mutate(file);
    }
  };

  const handleGenerate = () => {
    setShowProgressModal(true);
    setProgressStage(0);
    setCompletedCalendar(null);
    generateMutation.mutate();
  };

  const handleViewCalendar = () => {
    if (completedCalendar) {
      navigate(`/calendar/${completedCalendar.id}`);
    }
  };

  const progressStages = [
    { id: 0, label: 'Initializing...', icon: Loader2 },
    { id: 1, label: 'Analyzing personas & subreddits', icon: Users },
    { id: 2, label: 'Generating content angles', icon: Sparkles },
    { id: 3, label: 'Creating AI-powered posts', icon: Hash },
    { id: 4, label: 'Finalizing calendar', icon: CalendarIcon },
    { id: 5, label: 'Complete!', icon: CheckCircle }
  ];

  return (
    <div className="w-full max-w-3xl mx-auto px-2 sm:px-0">
      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 animate-zoom-in">
            {/* Header */}
            <div className="text-center mb-6">
              {progressStage < 5 ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-reddit-orange to-orange-500 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Generating Your Calendar</h2>
                  <p className="text-gray-500 text-sm mt-1">This may take a minute...</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center animate-zoom-in">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Calendar Ready!</h2>
                  <p className="text-gray-500 text-sm mt-1">Your content calendar has been generated</p>
                </>
              )}
            </div>

            {/* Progress Steps */}
            <div className="space-y-3 mb-6">
              {progressStages.slice(1).map((stage) => {
                const StageIcon = stage.icon;
                // For the Complete stage (id: 5), mark it complete when progressStage === 5
                const isComplete = progressStage > stage.id || (stage.id === 5 && progressStage === 5);
                const isActive = progressStage === stage.id && stage.id !== 5;
                const isPending = progressStage < stage.id;

                return (
                  <div
                    key={stage.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                      isActive ? 'bg-orange-50 border border-reddit-orange' :
                      isComplete ? 'bg-green-50 border border-green-200' :
                      'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-reddit-orange text-white' :
                      isComplete ? 'bg-green-500 text-white' :
                      'bg-gray-200 text-gray-400'
                    }`}>
                      {isComplete ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : isActive ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <StageIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${
                      isActive ? 'text-reddit-orange' :
                      isComplete ? 'text-green-700' :
                      'text-gray-400'
                    }`}>
                      {stage.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Summary when complete */}
            {progressStage === 5 && completedCalendar && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {completedCalendar.summary?.totalPosts || completedCalendar.items?.length || 0}
                    </div>
                    <div className="text-xs text-gray-500">Posts Created</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {completedCalendar.summary?.totalComments || 0}
                    </div>
                    <div className="text-xs text-gray-500">Comments</div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Button */}
            {progressStage === 5 ? (
              <button
                onClick={handleViewCalendar}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-reddit-orange text-white font-medium rounded-full hover:bg-reddit-orangeLight transition-colors"
              >
                <Eye className="w-5 h-5" />
                <span>View Calendar</span>
              </button>
            ) : (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                  <Sparkles className="w-4 h-4 text-reddit-orange animate-pulse" />
                  <span>AI is crafting your content...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 mb-3 sm:mb-4"
        >
          <ArrowLeft className="w-4 h-4 flex-shrink-0" />
          <span>Back to Dashboard</span>
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Create New Content Plan</h1>
        <p className="text-gray-600 text-sm sm:text-base mt-1">
          Upload your company info to generate a weekly Reddit content calendar
        </p>
      </div>

      {/* Progress steps */}
      <div className="flex items-center justify-between mb-6 sm:mb-8 overflow-x-auto pb-2">
        {[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Review' },
          { num: 3, label: 'Generate' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center flex-shrink-0">
            <div className={`
              flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-medium flex-shrink-0
              ${step >= s.num ? 'bg-reddit-orange text-white' : 'bg-gray-200 text-gray-500'}
            `}>
              {step > s.num ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : s.num}
            </div>
            <span className={`ml-1.5 sm:ml-2 text-xs sm:text-sm whitespace-nowrap ${step >= s.num ? 'text-gray-900' : 'text-gray-500'}`}>
              {s.label}
            </span>
            {idx < 2 && (
              <div className={`w-8 sm:w-16 md:w-24 h-0.5 mx-2 sm:mx-4 flex-shrink-0 ${step > s.num ? 'bg-reddit-orange' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Upload Company Info File</h2>
          
          <FileUpload
            onFileSelect={handleFileSelect}
            selectedFile={file}
            onClear={() => {
              setFile(null);
              setPreview(null);
            }}
            isLoading={parseMutation.isPending}
          />

          {preview?.validation && !preview.validation.valid && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-800 font-medium mb-2">
                <AlertCircle className="w-5 h-5" />
                <span>Validation Errors</span>
              </div>
              <ul className="text-sm text-red-700 list-disc list-inside">
                {preview.validation.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleParse}
              disabled={!file || parseMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-reddit-orange text-white font-medium rounded-full hover:bg-reddit-orangeLight disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {parseMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                  <span>Parsing...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Review */}
      {step === 2 && preview && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Review Parsed Data</h2>

          {/* Company info */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Company</h3>
            <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">{preview.input?.company?.name}</p>
              <p className="text-sm text-gray-600 mt-1 break-all">{preview.input?.company?.website}</p>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="p-2 sm:p-4 bg-blue-50 rounded-lg text-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mx-auto mb-1" />
              <div className="text-lg sm:text-2xl font-bold text-gray-900">
                {preview.preview?.personas_count || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Personas</div>
            </div>
            <div className="p-2 sm:p-4 bg-purple-50 rounded-lg text-center">
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mx-auto mb-1" />
              <div className="text-lg sm:text-2xl font-bold text-gray-900">
                {preview.preview?.subreddits_count || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Subreddits</div>
            </div>
            <div className="p-2 sm:p-4 bg-green-50 rounded-lg text-center">
              <Hash className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mx-auto mb-1" />
              <div className="text-lg sm:text-2xl font-bold text-gray-900">
                {preview.preview?.keywords_count || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600">Keywords</div>
            </div>
          </div>

          {/* Personas list */}
          {preview.input?.personas?.length > 0 && (
            <div className="mb-4 sm:mb-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Personas</h3>
              <div className="space-y-2">
                {preview.input.personas.map((persona) => (
                  <div key={persona.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <span className="font-medium text-gray-900">{persona.username}</span>
                      <span className="text-sm text-gray-500">{persona.tone}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {persona.tags?.map((tag) => (
                        <span key={tag} className="badge bg-gray-200 text-gray-600 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Options */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">Calendar Options</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-700 block mb-1">Week Start Date</label>
                <input
                  type="date"
                  value={options.weekStartDate}
                  onChange={(e) => setOptions({ ...options, weekStartDate: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label className="text-sm text-gray-700 block mb-1">Personas to Use</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={options.personaCount}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const numVal = parseInt(val) || 0;
                    const maxPersonas = preview?.input?.personas?.length || preview?.preview?.personas_count || 10;
                    
                    if (val !== '' && numVal > maxPersonas) {
                      setPersonaWarning(`Maximum ${maxPersonas} personas available in the uploaded file`);
                      setOptions({ ...options, personaCount: maxPersonas });
                    } else {
                      setPersonaWarning('');
                      setOptions({ ...options, personaCount: val === '' ? '' : numVal });
                    }
                  }}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    const max = preview?.input?.personas?.length || preview?.preview?.personas_count || 10;
                    setOptions({ ...options, personaCount: Math.min(Math.max(1, val), max) });
                    setPersonaWarning('');
                  }}
                  className={`input w-full ${personaWarning ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter number"
                />
                {personaWarning ? (
                  <p className="text-xs text-red-500 mt-1">{personaWarning}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">Max: {preview?.input?.personas?.length || preview?.preview?.personas_count || 'N/A'} available</p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-700 block mb-1">Posts per Week</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={options.postsPerWeek}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setOptions({ ...options, postsPerWeek: val === '' ? '' : parseInt(val) });
                  }}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setOptions({ ...options, postsPerWeek: Math.max(1, val) });
                  }}
                  className="input w-full"
                  placeholder="Enter number"
                />
              </div>

              <div>
                <label className="text-sm text-gray-700 block mb-1">Comments per Post</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={options.commentsPerPost}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setOptions({ ...options, commentsPerPost: val === '' ? '' : parseInt(val) });
                  }}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setOptions({ ...options, commentsPerPost: Math.max(0, val) });
                  }}
                  className="input w-full"
                  placeholder="Enter number"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
            <button
              onClick={() => setStep(1)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-full hover:bg-gray-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 flex-shrink-0" />
              <span>Back</span>
            </button>
            <button
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-reddit-orange text-white font-medium rounded-full hover:bg-reddit-orangeLight disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>Generate Calendar</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getNextMonday() {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
  today.setDate(today.getDate() + daysUntilMonday);
  return today.toISOString().split('T')[0];
}
